// CONTRACT:C2-RENDER-ENGINE.1.0
/** Entity store — the delta-by-`id` heart of the render engine's data plane.
 *
 *  Pure logic, no GPU. Holds CanonicalEnvelope entities keyed by `id`:
 *   - upsert: same `id` ⇒ update-in-place, never duplicate (delta-by-`id` contract).
 *   - staleness: `valid_time.stale` vs the controller clock dims then expires entities.
 *   - subscribe(filter, cb): emits EntityDelta batches OFF the render loop.
 *
 *  This file is FULLY UNIT-TESTED. The GPU wiring (engine.ts) consumes its output.
 */

import type {
  CanonicalEnvelope,
  EntityDelta,
  EntityFilter,
  EntityView,
  Unsubscribe,
} from "../../contracts-ts/src/index.ts";

/** Liveness of an entity relative to the current clock and its `valid_time.stale`. */
export type Staleness = "fresh" | "stale" | "expired";

/** A stored entity plus engine-side derived state the layers read at symbolize time. */
export interface StoredEntity {
  readonly entity: CanonicalEnvelope;
  /** Derived from the last `setClock(now)`; recomputed lazily on read/tick. */
  readonly staleness: Staleness;
}

/** Staleness windows: when an entity dims, and when it is pruned. */
export interface StaleConfig {
  /** Window after `stale` during which the entity is "stale" (dimmed) but kept.
   *  A visual hint for the smear ramp; does NOT gate pruning. Default 60s. */
  dimWindowMs: number;
  /** Window after `stale` after which the entity is EXPIRED and PRUNED from the
   *  store. Default `Infinity` (ALL — never expire): staleness is a CLIENT-SIDE
   *  filter the operator dials down (TRACKED-TASK:TODO.md#log-scale-staleness-slider
   *  log-scale staleness slider), not a
   *  silent drop. A live feed whose data carries short stale windows — or a
   *  replayed corpus stamped with fixed past timestamps — therefore stays on the
   *  map by default; the operator chooses how far back to show. */
  expireWindowMs: number;
}

const DEFAULT_STALE: StaleConfig = { dimWindowMs: 60_000, expireWindowMs: Infinity };

interface Subscription {
  filter: EntityFilter;
  cb: (view: EntityView) => void;
  /** IVM: the subscriber's live filtered set, maintained O(1) per delta in {@link EntityStore.emit}.
   *  This replaces the old per-emit `filtered()` full-store scan, so the STORE's fan-out work is
   *  O(1)/delta (a burst of N single-entity upserts costs O(N) in the store, not O(N²)). NOTE: the
   *  Engine still delivers ONE deferred callback per emit ({@link ./engine.ts} subscribe), so a
   *  consumer that reads `entities` still pays O(matching) per delivery; a size-only consumer (reads
   *  `size`) pays O(1). Coalescing the per-emit deliveries into one is a separate follow-up.
   *  Invariant: this map equals `filtered(filter)` over the current store at all times. */
  readonly view: Map<string, CanonicalEnvelope>;
}

/** Build the {@link EntityView} handed to a subscriber. Both `size` and `entities` read the LIVE
 *  filtered set at ACCESS time, so a single delivered view is always self-consistent
 *  (`size === entities.length`). `size` stays O(1) (`Map.size`); `entities` materializes lazily +
 *  memoized, so a size-only consumer never forces the O(matching) snapshot. For a synchronous
 *  consumer this is the emit-time set (identical to the old `filtered()`); a consumer that reads it
 *  from a callback deferred past a later batch sees the post-batch set — both production consumers
 *  (track count, alert log) are count/set-idempotent, so the final observed state is unchanged. */
function makeView(sub: Subscription, deltas: EntityDelta[]): EntityView {
  let cached: CanonicalEnvelope[] | null = null;
  return {
    deltas,
    get size(): number {
      return sub.view.size;
    },
    get entities(): CanonicalEnvelope[] {
      return (cached ??= [...sub.view.values()]);
    },
  };
}

/** Parse an RFC3339 timestamp to epoch ms; NaN if unparseable. */
function toEpochMs(ts: string): number {
  return Date.parse(ts);
}

function stalenessOf(
  entity: CanonicalEnvelope,
  now: number,
  cfg: StaleConfig,
): Staleness {
  const stale = toEpochMs(entity.valid_time.stale);
  if (Number.isNaN(stale)) return "fresh";
  if (now < stale) return "fresh";
  // Past `stale`: kept-but-"stale" until the EXPIRE window (default Infinity ⇒
  // never), then pruned. The dim ramp (a visual) is handled at symbolize time;
  // category-wise everything past `stale` and before expiry is "stale".
  if (now < stale + cfg.expireWindowMs) return "stale";
  return "expired";
}

function matches(entity: CanonicalEnvelope, filter: EntityFilter): boolean {
  if (filter.ids && !filter.ids.includes(entity.id)) return false;
  if (filter.kinds && !filter.kinds.includes(entity.kind)) return false;
  if (filter.where) {
    for (const [k, v] of Object.entries(filter.where)) {
      if ((entity.properties as Record<string, unknown>)[k] !== v) return false;
    }
  }
  return true;
}

/** Delta-by-`id` entity store. Renderer-agnostic, no GPU, fully testable. */
export class EntityStore {
  private readonly map = new Map<string, CanonicalEnvelope>();
  private readonly subs = new Set<Subscription>();
  private cfg: StaleConfig;
  private now = 0;

  constructor(cfg: Partial<StaleConfig> = {}) {
    this.cfg = { ...DEFAULT_STALE, ...cfg };
  }

  /** Number of live (non-expired-and-pruned) entities currently held. */
  get size(): number {
    return this.map.size;
  }

  /** Apply a batch delta-by-`id`. Same `id` ⇒ update-in-place (NOT a duplicate).
   *  Returns the deltas produced, which are also fanned out to subscribers. */
  upsert(entities: CanonicalEnvelope[]): EntityDelta[] {
    const deltas: EntityDelta[] = [];
    for (const e of entities) {
      // Same-id replaces in place: Map.set dedupes by key, so an update of an
      // existing id mutates rather than appending a second record.
      this.map.set(e.id, e);
      deltas.push({ op: "upsert", id: e.id, entity: e });
    }
    if (deltas.length) this.emit(deltas);
    return deltas;
  }

  /** Explicit removal by id. Returns the remove deltas actually applied. */
  remove(ids: string[]): EntityDelta[] {
    const deltas: EntityDelta[] = [];
    for (const id of ids) {
      if (this.map.delete(id)) deltas.push({ op: "remove", id });
    }
    if (deltas.length) this.emit(deltas);
    return deltas;
  }

  get(id: string): CanonicalEnvelope | undefined {
    return this.map.get(id);
  }

  /** Reconfigure the staleness windows live (the operator's staleness-filter
   *  slider). Re-evaluates immediately against the current clock: a TIGHTENED
   *  expire window prunes entities that are now past it (emitting remove deltas),
   *  a WIDENED one is reflected on the next upsert/tick. Returns any prune deltas. */
  setStaleConfig(cfg: Partial<StaleConfig>): EntityDelta[] {
    this.cfg = { ...this.cfg, ...cfg };
    return this.setClock(this.now);
  }

  /** The active staleness windows (e.g. for the slider to render its current state). */
  staleConfig(): Readonly<StaleConfig> {
    return this.cfg;
  }

  /** Current liveness of an entity vs the last `setClock`. */
  stalenessOf(id: string): Staleness | undefined {
    const e = this.map.get(id);
    if (!e) return undefined;
    return stalenessOf(e, this.now, this.cfg);
  }

  /** Snapshot of all held entities decorated with their current staleness. */
  snapshot(): StoredEntity[] {
    const out: StoredEntity[] = [];
    for (const entity of this.map.values()) {
      out.push({ entity, staleness: stalenessOf(entity, this.now, this.cfg) });
    }
    return out;
  }

  /** Advance the staleness clock. Entities past `stale + dimWindow` are EXPIRED:
   *  they are pruned from the store and emitted as `remove` deltas (dim/expire).
   *  Returns the remove deltas produced (newly expired entities). */
  setClock(now: number): EntityDelta[] {
    this.now = now;
    const expired: EntityDelta[] = [];
    for (const [id, e] of this.map) {
      if (stalenessOf(e, now, this.cfg) === "expired") {
        this.map.delete(id);
        expired.push({ op: "remove", id });
      }
    }
    if (expired.length) this.emit(expired);
    return expired;
  }

  /** Subscribe to filtered entity deltas. The cb fires synchronously here on
   *  mutation/tick — the ENGINE is responsible for scheduling this off the rAF
   *  hot path (microtask), so store fan-out stays decoupled from the render loop. */
  subscribe(filter: EntityFilter, cb: (view: EntityView) => void): Unsubscribe {
    // IVM: seed the subscriber's live filtered set once (a one-time O(N) prime scan), then
    // maintain it incrementally in emit(). This replaces the per-emit O(N) filtered() scan that
    // made a bulk burst O(N²).
    const view = new Map<string, CanonicalEnvelope>();
    for (const e of this.map.values()) if (matches(e, filter)) view.set(e.id, e);
    const sub: Subscription = { filter, cb, view };
    this.subs.add(sub);
    // Prime with the current matching set (no deltas).
    cb(makeView(sub, []));
    return () => {
      // subs.delete stops all future fan-out AND releases the store's reference to `sub`; its `view`
      // is then reachable only via any view closures already handed to pending deferred callbacks,
      // and is GC'd once they drain. Do NOT clear() it here — that would empty the map that a
      // still-pending callback's lazy `entities` getter reads from.
      this.subs.delete(sub);
    };
  }

  private emit(deltas: EntityDelta[]): void {
    for (const sub of this.subs) {
      // Maintain the subscriber's live filtered set O(1)/delta AND collect the deltas relevant to
      // this subscriber. Relevance rules are unchanged from the prior filtered()-based fan-out:
      //   - a matching upsert is forwarded (and enters the set);
      //   - an upsert that no longer matches (e.g. a where-filter property changed) silently
      //     LEAVES the set — not forwarded, exactly as the old filtered() re-scan would exclude it;
      //   - a remove is forwarded unless an ids-filter excludes it (a remove carries no kind, so a
      //     kinds-filter cannot exclude it — the old conservative behavior).
      const relevant: EntityDelta[] = [];
      for (const d of deltas) {
        if (d.op === "upsert") {
          if (matches(d.entity, sub.filter)) {
            sub.view.set(d.id, d.entity);
            relevant.push(d);
          } else {
            sub.view.delete(d.id);
          }
        } else if (!sub.filter.ids || sub.filter.ids.includes(d.id)) {
          sub.view.delete(d.id);
          relevant.push(d);
        }
      }
      if (!relevant.length) continue;
      sub.cb(makeView(sub, relevant));
    }
  }
}
